<script lang="ts">
    import vm, { JourneyNode } from '@journeys/Journeys.viewmodel';

    import HSLColors from '@shared/components/ColorPickers/HSLColorValues';
    import { NodeFrame, NodeIcon } from '../NodePartials/NodeAssets';

    // components
    import NodeTemplate from '../NodePartials/NodeTemplate.vue';

    // assets
    import { ConnectFromSVG, ConnectToSVG } from '@shared/assets/vector/ui/IconsUI';
    import { NodeType, NodeConditionVariation } from '../../Enums';
    import { computed, reactive, onUnmounted, onMounted } from 'vue';
    import { DynamicCondition, DynamicDelay, IfSVG, TinyClockSVG, TinyDelayBadge, TinyConditionBadge } from '@journeys/components/NodePartials/NodeAssets';
    import { uid } from '@shared/utils';

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'Jorney_Node',
    };
    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    const props = withDefaults(
            defineProps<{
                node: JourneyNode;
                moveNode?: Func;
                startConnect?: Func;
                startResize?: Func;
            }>(),
            {
                moveNode: () => {},
                startConnect: () => {},
                startResize: () => {},
            }
        ),
        state = reactive({
            dynamicWidth: false,
            editTitle: false,
        }),
        defaults = {
            node_width: props.node.data.type === NodeType.Condition ? 75 : 95,
            group_width: 95,
        };

    const { editor } = vm;

    let visible_items: any = reactive([]);
    const node_height = computed(() => {
        if (props.node.i !== NodeIcon.DynamicCondition && props.node.i !== NodeIcon.DynamicDelay) return props.node.h;

        let node = props.node,
            mod = node.data.segment?.Segments.length || 0;

        if (node.data.delay_d) state.dynamicWidth = ++mod === 1;

        if (!mod) {
            delay_el && resize_obs.unobserve(delay_el);

            delay_el = undefined;
            visible_items = [];

            node.w = 60;
            return (node.h = 16);
        } else {
            if (!delay_el || (mod > 1 && node.w !== defaults.group_width) || state.dynamicWidth)
                node.w = mod === 1 && node.data.delay_d ? delay_el?.lastNodeWidth || defaults.node_width : defaults.group_width;

            state.dynamicWidth ? delay_el && resize_obs.observe(delay_el) : delay_el && resize_obs.unobserve(delay_el);
        }

        node.h = 23 * (mod > 4 ? 4 : mod);

        visible_items = node.data.segment?.Segments.slice(0, 3);

        return node.h;
    });

    let temp_PropertySegmentOperators = ['==', '!=', '>', '<', 'cnt', '!cnt', '>=', '<=', '', 'null', '!null', '', 'sw', '!sw'];
    const conditionTextFactory = {
        SegmentGroup: ({ Bool, Segments }: { Bool: number; Segments: any[] }) =>
            `${Bool === 1 ? 'all' : 'any'} <tspan>in</tspan> Group <tspan>(+${Segments.length})</tspan>`,

        PropertySegment: ({ Operator }: { Operator: number }) =>
            `Property <tspan>${temp_PropertySegmentOperators[Operator]}</tspan>${Operator !== 9 && Operator !== 10 ? ' Val' : ''}`,

        AgeSegment: ({ MinAge, MaxAge }: { MinAge?: number; MaxAge?: number }) =>
            `${MinAge ? 'Age <tspan>></tspan> ' + MaxAge : ''}${MinAge && MaxAge ? ' & ' : !MinAge && !MaxAge ? 'Age: <tspan>undefined</tspan>' : ''}${
                MaxAge ? 'Age <tspan><</tspan> ' + MaxAge : ''
            }`,

        TagSegment: ({ HasTags, Bool, TagIds }: { HasTags: boolean; Bool: number; TagIds: string[] }) =>
            `${HasTags ? '' : '!'}has ${Bool === 1 ? 'all' : 'any'} Tags <tspan>(+${TagIds.length})</tspan>`,

        RecipientSegment: ({ ReceivedMailing }: { ReceivedMailing: boolean }) => `${ReceivedMailing ? '' : '!'}Recieved <tspan>=></tspan> Issue`,

        MemberSegment: ({ InList }: { InList: boolean }) => `${InList ? 'is' : 'is not'} <tspan>in</tspan> List`,

        SegmentMemberSegment: ({ InSegment }: { InSegment: boolean }) => `${InSegment ? 'is' : 'is not'} <tspan>in</tspan> Segment`,

        EventParticipantIssueSegment: ({ HasReceivedEmail, HasStatus, Statuses }: { HasReceivedEmail: boolean; HasStatus: boolean; Statuses: number[] }) =>
            `${HasReceivedEmail ? '' : '!'}Issue <tspan>=></tspan> ${HasStatus ? '' : '!'}Status <tspan>(+${Statuses.length})</tspan>`,

        EventParticipantSmsSegment: ({ HasReceivedSms, HasStatus, Statuses }: { HasReceivedSms: boolean; HasStatus: boolean; Statuses: number[] }) =>
            `${HasReceivedSms ? '' : '!'}Sms <tspan>=></tspan> ${HasStatus ? '' : '!'}Status <tspan>(+${Statuses.length})</tspan>`,

        EventParticipantSegment: ({ HasReceivedEmail, HasStatus, Statuses }: { HasReceivedEmail: boolean; HasStatus: boolean; Statuses: number[] }) =>
            `${HasReceivedEmail ? '' : '!'}Issue <tspan>=></tspan> ${HasStatus ? '' : '!'}Status <tspan>(+${Statuses.length})</tspan>`,

        InteractionSegment: ({ HasInteractions }: { HasInteractions: boolean }) => `${HasInteractions ? '' : '!'}Interacted <tspan>=></tspan> Issue`,

        SurveyRespondentSegment: ({ HasReceivedEmail, HasResponded }: { HasReceivedEmail: boolean; HasResponded: boolean }) =>
            `${HasReceivedEmail ? '' : '!'}Issue <tspan>=></tspan> ${HasResponded ? '' : '!'}Survey`,
    };

    type CTFKeys = keyof typeof conditionTextFactory;

    let conditionColors = ['#E82C58', '#636', '#c60', '#ab5468', '#3333ff', '#c39', '#63f', '#939', '#903', '#c00'],
        delay_clr = '#4958A7';
    const conditionText = computed(() => {
        if (props.node.data.type !== NodeType.Condition) return {};

        return (
            props.node.data.segment?.Segments.reduce(
                (acc: { [k in CTFKeys]?: any }, seg: any, idx: number) => (
                    (acc[seg.Type as CTFKeys] = {
                        clr: conditionColors[idx],
                        text: conditionTextFactory[seg.Type as CTFKeys](seg),
                    }),
                    acc
                ),
                {}
            ) || {}
        );
    });

    interface ExtendedElement extends HTMLElement {
        text_el?: any;
        obs?: any;
        data?: any;
        up_prop: {
            [k: string]: any;
        };
        lastWidth: number;
        lastNodeWidth: number;
    }

    const resize_callback: ResizeObserverCallback = (entries, observer: ResizeObserver | any) => {
        for (let entry of entries) {
            let ent = entry.target as ExtendedElement,
                w = ~~entry.contentRect.width,
                lw = ent.lastWidth ?? (ent.lastWidth = w),
                diff = 0;

            if ((diff = w - lw)) {
                ent.up_prop.width(diff);

                ent.lastWidth = w;
                ent.lastNodeWidth = props.node.w;
            }
        }
    };

    const resize_obs = new ResizeObserver(resize_callback) as ResizeObserver & { targets: any; o: any };
    resize_obs.targets = {};
    resize_obs.o = { observe: resize_obs.observe.bind(resize_obs), unobserve: resize_obs.unobserve.bind(resize_obs) };

    resize_obs.observe = (el: Element & { uid: string }) => {
        let el_uid = el.uid;

        if (!resize_obs.targets[el_uid]) {
            resize_obs.targets[el_uid] = el;
            resize_obs.o.observe(el);

            return 1;
        }

        return 0;
    };

    resize_obs.unobserve = (el: Element & { uid: string }) => {
        if (!resize_obs.targets[el?.uid]) return 0;

        console.log(resize_obs.targets, el.uid);
        delete resize_obs.targets[el.uid];
        resize_obs.o.unobserve(el);

        return 1;
    };

    let delay_el: ExtendedElement | undefined;
    const procRef = (el: any) => {
        el = el?.querySelector('text');
        if (!el || el.uid) return;

        el.uid = uid();

        el.up_prop = {
            height: (mod: number = 0, nw: number) => (props.node.w = (nw = props.node.w + mod) >= 0 ? nw : 0),
            width: (mod: number = 0, nw: number) => (props.node.w = (nw = props.node.w + mod) >= 0 ? nw : 0),
        };
        resize_obs.observe(el);

        delay_el = el;
    };

    onUnmounted(() => {
        resize_obs.disconnect();
    });

    const editTitleInline = (ev: PointerEvent) => {
        state.editTitle = true;
    };
</script>

<template>
    <div
        :key="`jn${node.id}`"
        :id="`jn${node.id}`"
        class="node"
        :class="editor.tools.highlight[node.id] && 'highlight'"
        :data-id="node.id"
        data-nodetype="canvas-node"
        :style="`height: ${node_height}px;
         width: ${node.w}px;
         transform: translate3d(${node.x}px, ${node.y}px, 0)`">
        <div class="node-title-wrapper">
            <div class="fl align-fl-content-c align-fl-items-e" v-if="node.data.type === NodeType.Condition && node.data.var === NodeConditionVariation.Delay">
                <h4 class="node-title">
                    <span class="d-block text-nowrap">
                        {{ $Resources.DelayMin }}: {{ node.data.delay_d ? node.data.delay_d + 'd' : '' }} {{ node.data.delay_h ? node.data.delay_h + 'h' : '' }}
                        {{ node.data.delay_m ? node.data.delay_m + 'm' : '' }}
                    </span>
                    <span class="d-block text-nowrap" v-if="node.data.delay_interval_d || node.data.delay_interval_h || node.data.delay_interval_m">
                        {{ $Resources.DelayMax }}: {{ node.data.delay_interval_d ? node.data.delay_interval_d + 'd' : '' }}
                        {{ node.data.delay_interval_h ? node.data.delay_interval_h + 'h' : '' }}
                        {{ node.data.delay_interval_m ? node.data.delay_interval_m + 'm' : '' }}
                    </span>
                </h4>
            </div>
            <div class="fl align-fl-content-c align-fl-items-e">
                <h4 class="ordinal" v-if="node.runtime.ordinal || node.data.ordinal">{{ node.runtime.ordinal || node.data.ordinal }}</h4>
                <h4
                    v-if="node.data.title"
                    class="node-title"
                    ref="nodeTitleRef"
                    :contenteditable="state.editTitle"
                    @input="
                        (ev: Event) => ((ev as KeyboardEvent).key === 'Enter' && ev.preventDefault(), (node.data.title = (ev.target as HTMLElement).innerText))
                    "
                    @pointerdown.left.stop="editor.state.activeTool === 'text' && (state.editTitle = true)"
                    @blur="state.editTitle = false">
                    {{ node.data.title }}
                </h4>
            </div>
        </div>
        <template v-if="node.i !== NodeIcon.DynamicDelay && node.i !== NodeIcon.DynamicCondition">
            <NodeTemplate
                :id="node.id"
                :frame="node.f"
                :frameColor="(HSLColors as Indexable<number, any>)[node.fc]"
                :icon="node.i"
                :iconColor="(HSLColors as Indexable<number, any>)[node.ic]"
                :height="node.h"
                :width="node.w"
                :type="node.data.type"
                class="canvas-node"
                :class="node.f === NodeFrame.RoundedRect && 'frame-offset-rect'"
                :data-id="node.id"
                data-nodetype="canvas-node"
                @pointerdown.left="(ev: any) => moveNode(ev, node)" />

            <g class="condition-node-info" v-if="node.data.type !== NodeType.Condition">
                <TinyDelayBadge
                    v-if="node.data.delay_d || node.data.delay_h || node.data.delay_m || (node.data.resendToExistingRecipients && node.data.TimeSpanSince)" />
                <TinyConditionBadge v-if="node.data.segment?.Segments?.length" />
            </g>
        </template>
        <template v-else>
            <g class="condition-node-list" :data-id="node.id" data-nodetype="canvas-node" @pointerdown.left="(ev: any) => moveNode(ev, node)">
                <svg width="50" height="16" v-if="!node.data.delay_d && !node.data.segment?.Segments?.length">
                    <rect width="50" height="16" fill="#231F20" rx="5" />
                    <text text-anchor="middle" fill="#f3e8e8" y="11" x="25" style="font-size: 9px">{{ $Resources.GenericEmptyLabel }}</text>
                </svg>
                <DynamicDelay
                    v-if="node.data.delay_d"
                    :ref="procRef"
                    :width="node.w"
                    :icon="TinyClockSVG"
                    :accentColor="delay_clr"
                    :text="`Wait ${node.data.delay_d} ${node.data.delay_d === 1 ? 'day' : 'days'}`" />
                <DynamicCondition
                    v-for="item of visible_items"
                    :width="node.w"
                    :icon="IfSVG"
                    :accentColor="conditionText[item.Type]?.clr"
                    :text="conditionText[item.Type]?.text" />
            </g>
        </template>

        <!-- node controls -->
        <div
            v-if="node.id === editor.tools.sel_keyObj || editor.tools.sel_count === 0 || (editor.tools.sel_count === 1 && editor.tools.sel_nodes[node.id])"
            class="node-ui">
            <div class="node-button center-left" @pointerdown.left="(ev: PointerEvent) => startConnect(ev, 'fr', node)">
                <ConnectFromSVG />
            </div>

            <div class="node-button center-right" @pointerdown.left="(ev: any) => startConnect(ev, 'to', node)">
                <ConnectToSVG />
            </div>
        </div>
    </div>
</template>

<style>
    .node-title {
        pointer-events: all;
    }

    .node-title:focus-visible {
        outline: none;
        background-color: #3c00ff0d;
    }

    /* nib: node info badge */
    .condition-node-info {
        --nib-icon-fill: #651742;
        --nib-fill: #c11e80;
        --nib-stroke: #651742;
        --nib-opacity: 0.15;
    }

    .condition-node-info .frame {
        opacity: var(--nib-opacity);
    }

    .condition-node-info .frame circle {
        fill: var(--nib-fill);
    }

    .condition-node-info .frame path {
        fill: var(--nib-stroke);
    }

    .condition-node-info .icon {
        fill: var(--nib-icon-fill);
    }

    .condition-node-list > svg {
        display: block;
        font-family: 'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif;
        pointer-events: none;
        position: relative;
        transform: translate(-50%, 0);
        left: 50%;
        margin: 3px 0;
    }

    .condition-node-info > svg {
        margin: 3px 2px;
        pointer-events: none;
    }

    .condition-node-info {
        display: flex;
        justify-content: center;
    }

    .condition-node-list tspan {
        font-size: 80%;
        fill: #c9a5a5;
    }

    .dev-thing {
        position: absolute;
        white-space: nowrap;
        text-align: center;
        transform: translate3d(-50%, 0, 0);
        top: calc(100% + 0.3rem);
        color: #6c4256;
        left: 50%;

        pointer-events: none;
    }

    h4.ordinal {
        font-size: 92%;
        font-weight: 600;
        margin-right: 0.1rem;
        white-space: nowrap;
    }

    h4.ordinal::first-letter {
        font-size: 82%;
        font-weight: 700;
        margin-right: 0.06rem;
    }

    .canvas:not(:has(.selection-area)) .node.highlight,
    .canvas:not(:has(.selection-area)) .node.selected {
        outline: 2px solid rgb(18 154 161 / 50%);
        outline-offset: 3px;
        border-radius: var(--border-radius);
    }
</style>
